import { useState, useEffect } from 'react';
import { createRouteTransitionSequence } from '../utils/transitionTimings';

/**
 * Hook para gerenciar transições entre rotas
 *
 * Controla a sequência de animações ao trocar de rota, criando transição suave:
 * fadeOut rota antiga → troca de rota → delay + fadeIn rota nova
 *
 * Fluxo ao trocar de rota:
 * 1. transitionStage = "exitingOld" (rota antiga desaparece com fadeOut)
 * 2. Aguarda ANIMATION_DURATIONS.BASE (300ms)
 * 3. displayLocation = nova rota, transitionStage = "enteringNew" (nova rota aparece com delay)
 * 4. Aguarda ANIMATION_DURATIONS.BASE * 2 (600ms)
 * 5. transitionStage = "fadeIn" (transição completa, estado normal)
 *
 * O displayLocation é mantido na rota antiga até o fadeOut completar, criando
 * efeito de crossfade suave. A detecção de mudança é feita pelo pathname.
 *
 * @param {Object} currentLocation - Objeto location do react-router com { pathname, search, hash, state, key }
 * @returns {Object} Estados para controle da transição
 * @returns {Object} returns.displayLocation - Location atualmente exibido (pode ser diferente durante transição)
 * @returns {string} returns.transitionStage - Estágio atual: "fadeIn" | "exitingOld" | "enteringNew"
 *
 * @example
 * const { displayLocation, transitionStage } = useRouteTransition(location);
 *
 * // Use displayLocation para renderizar (não location diretamente)
 * // Use transitionStage para aplicar classes CSS de animação
 * <Router location={displayLocation} className={transitionStage} />
 */
export const useRouteTransition = (currentLocation) => {
  const [displayLocation, setDisplayLocation] = useState(currentLocation);
  const [transitionStage, setTransitionStage] = useState('fadeIn');

  useEffect(() => {
    // Se a rota mudou
    if (currentLocation.pathname !== displayLocation.pathname) {
      const cleanup = createRouteTransitionSequence({
        onExitStart: () => {
          setTransitionStage('exitingOld');
        },
        onEnterStart: () => {
          setDisplayLocation(currentLocation);
          setTransitionStage('enteringNew');
        },
        onTransitionComplete: () => {
          setTransitionStage('fadeIn');
        },
      });

      return cleanup;
    }

    // Same-path navigation (only state/search/key changed, e.g. a
    // navigate(..., { replace: true }) that clears location.state): sync the
    // frozen displayLocation immediately without animating. Skipping this
    // strands the pages on a stale location — a same-path replace done to
    // clear consumed state never reaches useLocation(), so an effect that
    // reads that state re-fires and re-navigates forever (>100 replaceState/10s
    // throws SecurityError on iOS WebKit).
    //
    // Compare stable fields rather than the object reference: a future caller
    // that passes a shallow copy of location on every render would turn a
    // reference check into its own render loop.
    const sameLocationContent =
      currentLocation.key === displayLocation.key &&
      currentLocation.search === displayLocation.search &&
      currentLocation.state === displayLocation.state;

    if (!sameLocationContent) {
      setDisplayLocation(currentLocation);
    }

    return undefined;
  }, [currentLocation, displayLocation]);

  return { displayLocation, transitionStage };
};
